import { useRef, useState } from "react";
import { formatBytes, useClient, usePortal, FILE_LIMITS, type ClientFileRow, type FileUrlResponse } from "@app/portal-sdk";
import { Alert, Button, DataTable, EmptyState, IconButton, PageHeader, Stack, Typography } from "@app/ui-kit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Snackbar from "@mui/material/Snackbar";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { LoadingScreen } from "../components/LoadingScreen";
import { displayName, formatDate } from "../lib/format";
import { uploadClientFile } from "../lib/upload";
import { errorMessage, useApiQuery } from "../lib/useApiQuery";

/** Core feature: files shared between BKDP and the client. Downloads are audited. */
export const Documents = () => {
  const { api } = usePortal();
  const { client, canManage } = useClient();
  const files = useApiQuery<ClientFileRow[]>(
    () => api.request<ClientFileRow[]>(`/clients/${client.id}/files?category=document`),
    [api, client.id]
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ClientFileRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const open = async (file: ClientFileRow) => {
    setError(null);
    try {
      const { url } = await api.request<FileUrlResponse>(`/clients/${client.id}/files/${file.id}/url`);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const upload = async (file: File) => {
    if (file.size > FILE_LIMITS.documentMaxBytes) {
      setError("Files must be 100 MB or smaller");
      return;
    }
    setError(null);
    setUploading("Preparing…");
    try {
      const row = await uploadClientFile(api, client.id, file, "document", (step) =>
        setUploading(step === "upload" ? `Uploading ${file.name}…` : step === "complete" ? "Finishing…" : "Preparing…")
      );
      files.setData((rows) => [row, ...(rows ?? [])]);
      setToast(`${row.name} uploaded`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploading(null);
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    setError(null);
    try {
      await api.request<void>(`/clients/${client.id}/files/${pendingDelete.id}`, { method: "DELETE" });
      files.setData((rows) => (rows ?? []).filter((r) => r.id !== pendingDelete.id));
      setToast(`${pendingDelete.name} deleted`);
      setPendingDelete(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadButton = canManage ? (
    <>
      <Button variant="contained" onClick={() => fileInput.current?.click()} disabled={uploading !== null}>
        Upload
      </Button>
      <input
        ref={fileInput}
        type="file"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void upload(file);
        }}
      />
    </>
  ) : undefined;

  const rows = files.data ?? [];

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow={client.name}
        title="Documents"
        subtitle="Deliverables, briefings and working files BKDP has shared with you."
        actions={uploadButton}
      />
      {/* SHL-1: said where a person lands, not on the card. Documents live under this client alone; the store, the query and the router all say so. */}
      <Typography variant="body2" sx={{ color: "text.secondary", mt: -2 }} data-testid="documents-audience">Only people in your organization can see these.</Typography>
      {uploading && (
        <Stack spacing={1}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {uploading}
          </Typography>
          <LinearProgress color="secondary" />
        </Stack>
      )}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {files.error && <Alert severity="error">{files.error}</Alert>}

      {files.loading && !files.data ? (
        <LoadingScreen />
      ) : rows.length === 0 ? (
        <EmptyState
          eyebrow="Documents"
          title="No documents yet"
          description={canManage ? "Upload the first deliverable to share it with the client." : "Files shared with you will appear here."}
          action={uploadButton}
        />
      ) : (
        <DataTable
          columns={[
            { key: "name", label: "Name" },
            { key: "size", label: "Size" },
            { key: "uploaded", label: "Uploaded" },
            { key: "by", label: "By" },
            ...(canManage ? [{ key: "actions", label: "" }] : [])
          ]}
          rows={rows.map((file) => ({
            name: (
              <Link component="button" underline="hover" onClick={() => void open(file)} sx={{ fontWeight: 600, textAlign: "left" }}>
                {file.name}
              </Link>
            ),
            size: formatBytes(file.size),
            uploaded: formatDate(file.createdAt),
            by: displayName(file.uploadedBy),
            actions: canManage ? (
              <IconButton size="small" aria-label={`Delete ${file.name}`} onClick={() => setPendingDelete(file)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            ) : null
          }))}
        />
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={`Delete ${pendingDelete?.name ?? "this file"}?`}
        description="The file is removed from the portal for everyone."
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={() => void remove()}
        onClose={() => setPendingDelete(null)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Stack>
  );
};
